import React from 'react';
import { Warning } from '@phosphor-icons/react';
import HelpApiLayout from '../../../../components/layout/HelpApiLayout';
import HelpSidebar from '../../../../components/layout/HelpSidebar';
import ArticleNavigation from '../../../../components/layout/docs/ArticleNavigation';
import LastUpdated from '../../../../components/layout/docs/LastUpdated';
import { getHelpArticleRoute } from '../../../../constants/routes';
import { getHelpRoute } from '../../../../utils/navigation';
import './HelpCategoryPage.css';

/**
 * Top up your group Article Page
 */
const TopUpGroup = () => {
  return (
    <HelpApiLayout type="help" sidebar={<HelpSidebar />}>
      <div className="help-category-page">
        <div className="help-category-container">
          <main className="help-category-main">
            <div className="article-content">
              <header className="article-header">
                <h1 className="article-title">Top up your group</h1>
                <p className="article-subtitle">
                  Learn how to add funds to a shared group wallet, either by transferring instantly from your personal WeSplit account or by depositing from an external crypto source.
                </p>
              </header>

              <div className="article-separator"></div>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Overview</h2>
                  <p className="article-section-intro">
                    To pay for shared expenses or reach a collective financial goal, members need to fund the group wallet. WeSplit provides two flexible ways to do this: moving funds internally or depositing externally.
                  </p>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Method 1: From Your WeSplit Wallet</h2>
                  <p className="article-section-intro">
                    The fastest way to add funds is to move them directly from your personal WeSplit balance to the group.
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Open the Group:</strong> Navigate to the specific group you wish to fund.
                    </li>
                    <li>
                      <strong>Tap "Top Up":</strong> Click on the Top Up button located on the group dashboard.
                    </li>
                    <li>
                      <strong>Select Source:</strong> Choose "From my personal wallet".
                    </li>
                    <li>
                      <strong>Confirm Transfer:</strong> Enter the amount you wish to contribute and confirm. The funds are moved instantly and recorded in the group activity.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Method 2: From an External Wallet</h2>
                  <p className="article-section-intro">
                    You can also send funds directly to the group wallet from an outside exchange (like Binance or Coinbase) or a different crypto wallet (like Phantom).
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Get Group Address:</strong> Inside the group, go to "Settings". Copy the Group Public Address to your clipboard.
                    </li>
                    <li>
                      <strong>Initiate External Transfer:</strong> Open your external wallet or exchange app. Select "Withdraw" or "Send".
                    </li>
                    <li>
                      <strong>Send Funds:</strong> Paste the Group Public Address as the destination. Enter the amount of <strong>USDC</strong>. Verify you are on the <strong>Solana network</strong>. Confirm the transaction on the Solana network.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section">
                <div className="article-warning">
                  <div className="article-warning-header">
                    <Warning className="article-warning-icon" weight="fill" />
                    <h3 className="article-warning-title">USDC on Solana Only</h3>
                  </div>
                  <div className="article-warning-content">
                    <p>
                      WeSplit exclusively supports <strong>USDC</strong> on the <strong>Solana network</strong>.
                    </p>
                    <ul className="article-list-dots">
                      <li>
                        Do not send other cryptocurrencies (like Bitcoin or Ethereum) to this address.
                      </li>
                      <li>
                        Ensure you select the <strong>Solana network</strong> when sending <strong>USDC</strong>.
                      </li>
                      <li>
                        Sending unsupported assets may result in permanent loss of funds.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <div className="article-separator"></div>

              <LastUpdated />

              <ArticleNavigation
                previous={{
                  title: 'Manage your group',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'manage-group'))
                }}
                next={{
                  title: 'Withdraw from a group',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'withdraw-group'))
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default TopUpGroup;
